import { Form, Row } from 'antd';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Select, FormItemSelect, FormItemInputNumber } from '../..';
import { useGetCargosQuery } from '../../../services/cargoApi';
import { useGetCompaniesQuery } from '../../../services/companyApi';
import {
  Cargo as CargoMerchandiseType,
  CargoType as CargoTypeType,
  ProductGroup as ProductGroupType,
} from '../../../types';
import { booleanList } from '../../../utils/lists';
import { getCompanyDisplayName } from '../../../utils/formatters';
import {
  isNullOrUndefined,
  removeRepeatedItemsFromArray,
} from '../../../utils/utils';

type ProductFormProps = {
  product_key: number;
  readOnly: boolean;
  allowedCargo?: CargoTypeType[];
  operationTypes: any[];
  allowedOperators: any[];
  units: any[];
  formDocking?: any;
  noStyle?: boolean;
};

export const Product = styled.div`
  & .title {
    color: var(--neutral_medium);
    font-weight: bold;
    font-size: 18px;
    display: inline-block;
    margin-bottom: 15px;
  }
`;

function withCurrentOption(list: any[] | undefined, current: any) {
  if (isNullOrUndefined(current?.id) || isNullOrUndefined(current?.name)) {
    return list;
  }
  if (list?.some((item) => item.id === current.id)) {
    return list;
  }
  return [current, ...(list || [])];
}

export function ProductForm(props: ProductFormProps) {
  const {
    product_key,
    readOnly,
    allowedCargo,
    operationTypes,
    allowedOperators,
    units,
    formDocking,
    noStyle,
  } = props;

  const [isDirectDischarge, setIsDirectDischarge] = useState(
    formDocking.getFieldValue(['products', product_key, 'direct_discharge']) ===
      'true'
  );
  const [productUnit, setProductUnit] = useState(
    formDocking.getFieldValue(['products', product_key, 'unit'])
  );
  const [cargoNatureOfCargo, setCargoNatureOfCargo] = useState('');
  const [selectedProductGroupId, setSelectedProductGroupId] = useState(0);

  const CARGA_GERAL_ID = 1;
  const natureOfCargoId = Form.useWatch(
    ['operation_summary', 'nature_of_cargo', 'id'],
    formDocking
  );
  const [productGroupData, setProductGroupData] = useState<ProductGroupType[]>(
    []
  );

  const { data: storages } = useGetCompaniesQuery({
    company_type: 'STORAGE',
    page_size: 100,
  });

  const { data: cargosData } = useGetCargosQuery({ page_size: 999 });
  const allCargos: CargoMerchandiseType[] = cargosData?.results || [];

  const selectedCargoTypeName = Form.useWatch(
    ['products', product_key, 'cargo_type', 'name'],
    formDocking
  );

  const currentProductGroup = Form.useWatch(
    ['products', product_key, 'cargo_type', 'product_group'],
    formDocking
  );
  const currentCargo = Form.useWatch(
    ['products', product_key, 'cargo'],
    formDocking
  );
  const currentOperator = Form.useWatch(
    ['products', product_key, 'operator'],
    formDocking
  );
  const currentStevedoringOperator = Form.useWatch(
    ['products', product_key, 'stevedoring_operator'],
    formDocking
  );
  const currentStorage = Form.useWatch(
    ['products', product_key, 'storage'],
    formDocking
  );

  useEffect(() => {
    setProductGroupData(
      removeRepeatedItemsFromArray(
        allowedCargo?.flatMap((cargo) => cargo.product_group) || [],
        'id'
      )
    );

    const cargoProduct = formDocking.getFieldValue([
      'products',
      product_key,
      'cargo_type',
      'product_group',
      'nature_of_cargo',
      'name',
    ]);
    setCargoNatureOfCargo(cargoProduct);
  }, [allowedCargo]);

  function clearStorageProduct(): void {
    const allProducts = formDocking.getFieldValue('products');

    let productKey = 0; // simula a key do produto pois se o produto ainda não foi salvo, não tem como comparar pelo id
    const updatedProducts = allProducts.map((product: any) => {
      product.key = productKey++;
      if (product.key === product_key) {
        return {
          ...product,
          storage: null,
        };
      }
      return product;
    });

    formDocking.setFieldsValue({ products: updatedProducts });
  }

  function onChangeCargo(cargoName: any) {
    clearStorageProduct();
    formDocking.setFields([
      { name: ['products', product_key, 'cargo', 'id'], value: null },
    ]);
    if (isNullOrUndefined(cargoName)) {
      setCargoNatureOfCargo('');
      return;
    }

    const cargoSelected = allowedCargo?.find(
      (cargo) => cargo.name === cargoName
    );

    setCargoNatureOfCargo(
      cargoSelected?.product_group?.nature_of_cargo?.name || ''
    );
  }

  function onChangeDirectDischarge(evt: any) {
    if (evt === 'true') {
      setIsDirectDischarge(true);
    } else {
      setIsDirectDischarge(false);
    }
  }

  const unitsAddonAfter = (
    <Select
      disabled={readOnly}
      style={{ width: 80 }}
      defaultValue={productUnit}
      onChange={onChangeProductUnit}
      placeholder="un."
      optionLabelProp="label"
    >
      {units.map((unit) => (
        <Select.Option
          key={unit.value}
          value={unit.value}
          label={unit.abbreviation || unit.name}
        >
          {unit.name}
        </Select.Option>
      ))}
    </Select>
  );

  function onChangeProductUnit(value: any) {
    setProductUnit(value);
    const allProducts: any[] = formDocking.getFieldValue('products');

    const updatedProducts: any[] = allProducts.map((product, index): any => {
      if (index === product_key) {
        return {
          ...product,
          unit: value,
        };
      }
      return product;
    });
    formDocking.setFieldsValue({ products: updatedProducts });
  }

  function onSelectProductGroup(value: any) {
    setSelectedProductGroupId(value);
  }

  function cargoTypeOptionRenderer(option: Record<string, any>) {
    return (
      <Select.Option key={option.name} value={option.name}>
        {option.name}
      </Select.Option>
    );
  }

  // Prioriza o Nome Fantasia da empresa vinculada, com fallback para o nome do operador.
  function operatorOptionRenderer(option: Record<string, any>) {
    return (
      <Select.Option key={option.id} value={option.id}>
        {option.company?.trade_name?.trim() || option.name}
      </Select.Option>
    );
  }

  // Função para filtrar opções por texto parcial
  function filterOptionByText(input: string, option: any) {
    const optionText = `${option.children}`.toLowerCase();
    return optionText.includes(input.toLowerCase());
  }

  return (
    <Product>
      <Row gutter={30} align="bottom">
        <FormItemSelect
          colSpan={6}
          name={[product_key, 'cargo_type', 'product_group', 'id']}
          label="Grupo do produto"
          dataList={withCurrentOption(productGroupData, currentProductGroup)}
          onSelect={onSelectProductGroup}
          showSearch
          filterOption={filterOptionByText}
          allowClear
        />
        <FormItemSelect
          colSpan={6}
          name={[product_key, 'cargo_type', 'name']}
          label="Tipo de mercadoria"
          allowClear
          showSearch
          notFoundContent="Não foram encontradas mercadorias de acordo com os filtros informados"
          dataList={allowedCargo?.filter(
            (cargo) => cargo.product_group.id === selectedProductGroupId
          )}
          disabled={readOnly}
          onSelect={(e) => onChangeCargo(e)}
          noStyle={noStyle}
          filterOption={filterOptionByText}
          optionRenderer={cargoTypeOptionRenderer}
        />
        <FormItemSelect
          colSpan={6}
          name={[product_key, 'cargo', 'id']}
          label="Mercadoria"
          allowClear
          showSearch
          notFoundContent="Selecione o tipo de mercadoria"
          disabled={readOnly || isNullOrUndefined(selectedCargoTypeName)}
          noStyle={noStyle}
          filterOption={filterOptionByText}
          dataList={withCurrentOption(
            allCargos.filter(
              (c) => c.cargo_type?.name === selectedCargoTypeName
            ),
            currentCargo
          )}
          optionRenderer={(option: Record<string, any>) => {
            const cargo = option as CargoMerchandiseType;
            return (
              <Select.Option key={cargo.id} value={cargo.id}>
                {cargo.name}
                {cargo.ncm ? ` — NCM ${cargo.ncm}` : ' — sem NCM'}
              </Select.Option>
            );
          }}
        />
        <FormItemInputNumber
          colSpan={6}
          name={[product_key, 'quantity']}
          label="Quantidade"
          readOnly={readOnly}
          addonAfter={unitsAddonAfter}
          noStyle={noStyle}
        />
      </Row>
      <Row gutter={30} align="bottom">
        <FormItemSelect
          colSpan={8}
          name={[product_key, 'operation_type']}
          label="Sentido da operação"
          notFoundContent="Não foi encontrado nenhum sentido da operação"
          dataList={operationTypes}
          showSearch
          filterOption={filterOptionByText}
          disabled={readOnly}
          noStyle={noStyle}
          allowClear
        />
        <FormItemSelect
          colSpan={8}
          name={[product_key, 'operator', 'id']}
          label="Operador de Capatazia"
          notFoundContent="Não foi encontrado nenhum operador"
          dataList={withCurrentOption(allowedOperators, currentOperator)}
          showSearch
          filterOption={filterOptionByText}
          disabled={readOnly}
          noStyle={noStyle}
          allowClear
          optionRenderer={operatorOptionRenderer}
        />
        <FormItemSelect
          colSpan={8}
          name={[product_key, 'stevedoring_operator', 'id']}
          label="Operador de Estiva"
          notFoundContent="Não foi encontrado nenhum operador"
          dataList={withCurrentOption(
            allowedOperators,
            currentStevedoringOperator
          )}
          showSearch
          filterOption={filterOptionByText}
          disabled={readOnly || natureOfCargoId !== CARGA_GERAL_ID}
          noStyle={noStyle}
          allowClear
          optionRenderer={operatorOptionRenderer}
        />
      </Row>
      <Row gutter={30} align="bottom">
        <FormItemSelect
          colSpan={8}
          name={[product_key, 'direct_discharge']}
          label="Carga direta?"
          dataList={booleanList}
          disabled={readOnly}
          noStyle={noStyle}
          tooltip="Não será necessário armazenamento"
          onSelect={(evt) => onChangeDirectDischarge(evt)}
          allowClear
        />
        {!isDirectDischarge && (
          <FormItemSelect
            colSpan={8}
            name={[product_key, 'tanking_confirmed']}
            label="Armazenamento confirmado?"
            dataList={booleanList}
            disabled={readOnly}
            noStyle={noStyle}
            allowClear
          />
        )}
        {!isDirectDischarge && (
          <FormItemSelect
            colSpan={8}
            name={[product_key, 'storage', 'id']}
            label="Local de armazenamento"
            tooltip="Habilita local de acordo com o tipo de mercadoria selecionado"
            dataList={withCurrentOption(storages?.results, currentStorage)}
            disabled={readOnly || isNullOrUndefined(cargoNatureOfCargo)}
            showSearch
            filterOption={filterOptionByText}
            allowClear
            noStyle={noStyle}
            optionRenderer={(option: Record<string, any>) => (
              <Select.Option key={option.id} value={option.id}>
                {getCompanyDisplayName(option)}
              </Select.Option>
            )}
          />
        )}
      </Row>
    </Product>
  );
}
